/**
 * Tranche rows of the Repayment Plan section block (refValSeq 2646).
 *
 * The block's jsonData mirrors what the Repayment screen saves, which is
 * grouped by repayment term — `{ crdtPrpslSeq, terms: [{ rpymntTerm,
 * trancheRows: [...], ttlDsbmnt }] }` (SaveRepaymentPlanPayload) — not a
 * flat tranche-row list. Reading a term group as a tranche row rendered
 * every tranche with no month, a 0 amount and no repayment columns.
 *
 * Unwraps `terms` and `trancheRows` at any level; flat tranche rows are
 * returned unchanged. Each row keeps its term's `rpymntTerm` when it has none.
 */
export function flattenRepaymentTrancheRows(
  items: unknown[],
): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [];

  const visit = (item: unknown, rpymntTerm: unknown): void => {
    if (Array.isArray(item)) {
      item.forEach((entry) => visit(entry, rpymntTerm));
      return;
    }

    if (!item || typeof item !== 'object') {
      return;
    }

    const record = item as Record<string, unknown>;
    const term = record['rpymntTerm'] ?? rpymntTerm;

    if (Array.isArray(record['terms'])) {
      visit(record['terms'], term);
      return;
    }

    if (Array.isArray(record['trancheRows'])) {
      visit(record['trancheRows'], term);
      return;
    }

    rows.push(
      term !== undefined && record['rpymntTerm'] === undefined
        ? { ...record, rpymntTerm: term }
        : record,
    );
  };

  visit(items, undefined);

  return rows;
}

/**
 * Tranche rows rebuilt from the live saved plan — GET proposed disbursements
 * plus GET /rpymnt-plan/get-prpsd-rpymnt/{crdtPrpslSeq} (one installment per
 * entry) — in the same shape `flattenRepaymentTrancheRows` returns. The
 * Repayment Plan section block is only a snapshot taken on the first save and
 * is not refreshed by later re-calculate + save, so the report prefers this.
 *
 * Only tranches that have at least one saved installment become rows.
 */
export function buildRepaymentTrancheRowsFromSaved(
  disbursementsResponse: unknown,
  repaymentsResponse: unknown,
): Record<string, unknown>[] {
  const disbursements = extractList(disbursementsResponse, 'prpsdDsbmntRowDTOList');
  const repayments = extractList(repaymentsResponse, 'prpsdRpymntRowDTOList');

  const scheduleByTranche = new Map<number, Record<string, number>>();
  const quarterByTranche = new Map<number, string>();

  repayments.forEach((item) => {
    const trancheSeq = Number(item['prpsdDsbmntSeq'] ?? 0);
    const period = toDateString(item['schRpymntMth']);

    if (!(trancheSeq > 0) || !period) {
      return;
    }

    const schedule = scheduleByTranche.get(trancheSeq) ?? {};

    schedule[period] = roundAmount((schedule[period] ?? 0) + toAmount(item['prpsdRpymntAmnt']));
    scheduleByTranche.set(trancheSeq, schedule);

    const quarter = String(item['qrtr'] ?? '').trim();

    if (quarter && !quarterByTranche.has(trancheSeq)) {
      quarterByTranche.set(trancheSeq, quarter);
    }
  });

  return disbursements
    .map((item) => ({ item, seq: Number(item['prpsdDsbmntSeq'] ?? 0) }))
    .filter(({ seq }) => seq > 0 && scheduleByTranche.has(seq))
    .map(({ item, seq }) => ({
      prpsdDsbmntSeq: seq,
      quarter: quarterByTranche.get(seq) ?? '',
      month: toDateString(item['schMth']),
      prpsdDsbmntAmount: toAmount(item['prpsdDsbmntAmt'] ?? item['prpsdDsbmtAmt']),
      installmentsByPeriod: scheduleByTranche.get(seq) ?? {},
      rpymntTerm: Number(item['rpymntTerm'] ?? item['typ'] ?? item['termRefCdSeq'] ?? 0),
    }));
}

function extractList(response: unknown, wrapperKey: string): Record<string, unknown>[] {
  const source = (response as { data?: unknown } | null)?.data ?? response;
  const list = Array.isArray(source)
    ? source
    : Array.isArray((source as Record<string, unknown> | null)?.[wrapperKey])
      ? ((source as Record<string, unknown>)[wrapperKey] as unknown[])
      : [];

  return list.filter(
    (item): item is Record<string, unknown> =>
      !!item && typeof item === 'object' && Number((item as Record<string, unknown>)['delFlg'] ?? 0) !== 1,
  );
}

function toDateString(value: unknown): string {
  const date = String(value ?? '').slice(0, 10);

  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : '';
}

function toAmount(value: unknown): number {
  const amount = Number(value ?? 0);

  return Number.isFinite(amount) && amount >= 0 ? roundAmount(amount) : 0;
}

function roundAmount(value: number): number {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}
